import { useEffect, useMemo, useState } from 'react'

import {
  InvitationToCollaborate,
  onMyInvitations,
  onAcceptInvitation,
} from '../../lib/api'
import { Link, useNavigate } from 'react-router-dom'

export function Invitation({ invitationId }: { invitationId: string }) {
  const navigate = useNavigate()

  const [fetching, setFetching] = useState(true)
  const [accepting, setAccepting] = useState(false)

  const [invitations, setInvitations] = useState<
    Array<InvitationToCollaborate>
  >([])

  const acceptInvitation = async ({
    workspaceId,
    invitationId,
  }: {
    workspaceId: string
    invitationId: string
  }) => {
    setAccepting(true)
    try {
      await onAcceptInvitation({ workspaceId, invitationId })
      navigate('/workspace/' + workspaceId)
    } catch (err) {
      console.error(err)
    }
    setAccepting(false)
  }

  useEffect(() => {
    if (invitationId) {
      setFetching(true)
      onMyInvitations()
        .then((response) => {
          setInvitations(response.data.invitations)
          setFetching(false)
        })
        .catch(() => {
          setFetching(false)
        })
    }
  }, [invitationId])

  const invitation = useMemo(() => {
    return invitations.find((invitation) => invitation.id === invitationId)
  }, [invitations, invitationId])

  if (fetching) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
      </div>
    )
  }

  if (!invitation) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center p-8 max-w-md mx-auto bg-red-50 rounded-lg">
          <h2 className="text-xl font-semibold text-red-800 mb-2">
            Invitation Not Found
          </h2>
          <p className="text-red-600">
            The invitation ID {invitationId} could not be found.
          </p>
        </div>
      </div>
    )
  }

  if (invitation.status === 'accepted') {
    return (
      <div className="flex">
        <div className="text-center p-8 max-w-md mx-auto bg-red-50 rounded-lg">
          <h2 className="text-xl font-semibold ">
            You've already accepted this invitation
          </h2>
          <p className="text-red-600">
            <Link to={`/workspace/${invitation.workspaceId}`}>
              Go to business
            </Link>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screenpy-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden md:max-w-2xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {invitation.workspace.name}
          </h1>
          <p className="text-gray-600">You've been invited to collaborate!</p>
        </div>

        <div className="text-center">
          <button
            onClick={() =>
              acceptInvitation({
                workspaceId: invitation.workspaceId,
                invitationId: invitation.id,
              })
            }
            disabled={accepting}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 
                     transition-colors duration-200 font-medium focus:outline-none focus:ring-2 
                     focus:ring-blue-500 focus:ring-offset-2"
          >
            Accept Invitation
          </button>
        </div>
      </div>
    </div>
  )
}
